/**
 * Maps placement diagnostic question IDs (placementQuestions.js) to grammar
 * pillar IDs used in public/data/grammar/grammar-{level}.json.
 * Used for precision targeting: if a user fails e.g. p1, we inject the
 * corresponding grammar pillar (e.g. "Passiv Präsens" / passive) into their plan.
 *
 * Question IDs p1–p5 ≈ B1, p6–p10 ≈ B2, p11–p15 ≈ C1. Each entry lists the
 * pillar ID(s) per level that exist in the grammar JSON for that level.
 */

/** placement id → { B1?: string, B2?: string, C1?: string } */
const PLACEMENT_ID_TO_PILLAR_BY_LEVEL = {
  // B1 (1–5)
  p1: { B1: 'passive', B2: 'moods_and_voice_b2' }, // Passive, present
  p2: { B1: 'subjunctive', B2: 'moods_and_voice_b2' }, // Konjunktiv II
  p3: { B1: 'connectors' }, // obwohl, subordinate clause
  p4: { B1: 'participles-adjectives' }, // Partizip II
  p5: { B1: 'connectors' }, // deshalb, word order
  // B2 (6–10)
  p6: { B2: 'connectors_b2' }, // Two-part connectors
  p7: { B1: 'genitive', B2: 'prepositions_b2' }, // Genitiv preposition
  p8: { B1: 'passive', B2: 'moods_and_voice_b2' }, // Passive past
  p9: { B1: 'subjunctive', B2: 'moods_and_voice_b2' }, // Konjunktiv II past
  p10: { B1: 'participles-adjectives', B2: 'adjectives_b2' }, // Partizip II as attribute
  // C1 (11–15)
  p11: { C1: 'subjunctive_moods_c1' }, // Konjunktiv I indirect speech
  p12: { C1: 'nominal_vs_verbal_styles' }, // Nominalstil
  p13: { C1: 'passive_alternatives' }, // Passiversatzformen
  p14: { C1: 'participial_constructions_c1' }, // Partizip I attributive
  p15: {}, // Styling/register – no grammar pillar; optional: B2 stylistic_refinement_b2
};

/**
 * Returns the grammar pillar ID for a given placement question and target level,
 * or null if no mapping exists for that level.
 * @param {string} placementId - e.g. 'p1', 'p2'
 * @param {string} targetLevel - 'B1' | 'B2' | 'C1'
 * @returns {string|null}
 */
export function getPillarForPlacementQuestion(placementId, targetLevel) {
  const levelKey = (targetLevel || '').toUpperCase();
  const entry = PLACEMENT_ID_TO_PILLAR_BY_LEVEL[placementId];
  if (!entry || !levelKey) return null;
  return entry[levelKey] ?? null;
}

/**
 * Given placement answers and the list of questions, returns the set of grammar
 * pillar IDs the user failed (for the given target level). Used to front-load
 * these pillars in the study plan.
 * @param {Array<{ id: string }>} questions - PLACEMENT_QUESTIONS (or subset)
 * @param {Array<number|boolean|null>} answers - user answers (MC index or true/false for other types)
 * @param {string} targetLevel - 'B1' | 'B2' | 'C1'
 * @param {function} getCorrectIndex - (question) => correctIndex for MC; other types use true/false
 * @returns {string[]} unique pillar IDs to prioritise (may be empty)
 */
export function getFailedGrammarPillars(questions, answers, targetLevel, getCorrectIndex) {
  const failedPillars = new Set();
  if (!Array.isArray(questions) || !Array.isArray(answers) || !targetLevel) return [];

  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    if (q.type !== 'grammar') continue;
    const userAnswer = answers[i];
    if (userAnswer == null) continue;

    const isCorrect = q.exerciseType === 'multipleChoice'
      ? userAnswer === getCorrectIndex(q)
      : userAnswer === true;
    if (isCorrect) continue;

    const pillar = getPillarForPlacementQuestion(q.id, targetLevel);
    if (pillar) failedPillars.add(pillar);
  }

  return [...failedPillars];
}

export default {
  getPillarForPlacementQuestion,
  getFailedGrammarPillars,
  PLACEMENT_ID_TO_PILLAR_BY_LEVEL,
};
